import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {emitChangedWait, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel} from '@mui/material';
import {obsStyles} from '../obsStyle';

function ObsNeuroBestMotorResponse(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [bestMotorResponse, setBestMotorResponse] = React.useState(props.bestMotorResponse ?? props.response);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [bestMotorResponses, setBestMotorResponses] = React.useState([]);

    useEffect(() => {
        let eyes = [];
        if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._16_99) {
                eyes = [
                    {
                        id: '6',
                        description: t('Obey Commands'),
                        score: 6,
                        // default: true,
                    },
                    {
                        id: '5',
                        description: t('Localise Pain'),
                        score: 5,
                    },
                    {
                        id: '4',
                        description: t('Withdrawal to Pain'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Flexion to Pain'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Extension to Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._5_16) {
                eyes = [
                    {
                        id: '6',
                        description: t('Obeys Commands'),
                        score: 6,
                        // default: true,
                    },
                    {
                        id: '5',
                        description: t('Localises Pain'),
                        score: 5,
                    },
                    {
                        id: '4',
                        description: t('Flexion Withdrawal'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Decorticate Flexion'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Decerebrate Extension'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('No Response'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._1_5) {
                eyes = [
                    {
                        id: '6',
                        description: t('Spontaneous Movements'),
                        score: 6,
                        // default: true,
                    },
                    {
                        id: '5',
                        description: t('Withdraws to Touch'),
                        score: 5,
                    },
                    {
                        id: '4',
                        description: t('Withdrawal to Pain'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Abnormal Flexion to Pain'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Abnormal Extension to Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            }
        }
        setBestMotorResponses(eyes);
    }, [type]);

    function setDefaults() {
        let def = _.find(bestMotorResponses, (item) => !!item.default);
        // if (def) {
        setBestMotorResponse(def);
        // }
    }

    useEffect(() => {
        if (!bestMotorResponse) {
            setDefaults();
        }
    }, [bestMotorResponses]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (bestMotorResponse && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [bestMotorResponse, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    response: bestMotorResponse,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };
    useEffect(() => {
        emitChanged();
    }, [status, bestMotorResponse, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = undefined;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (bestMotorResponse) {
                newScore = bestMotorResponse.score;
            }
        }
        setScore(newScore);
    };
    useEffect(() => {
        updateScore();
    }, [status, bestMotorResponse]);

    const onResponseChange = (value) => {
        setBestMotorResponse(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    let scoreColor = 'neuro';
    if (score >= 4) {
        scoreColor = 'neuroLow';
    } else if (score === 3) {
        scoreColor = 'neuroMedium';
    } else if (score >= 1) {
        scoreColor = 'neuroHigh';
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Best Motor Response')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onResponseChange(value)}
                                    options={bestMotorResponses}
                                    value={bestMotorResponse}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        score,
                        renderScore: (
                            <div className={clsx([classes.scoreBadge, scoreColor])}>
                                {_.isUndefined(score) && <span>&nbsp;</span>}
                                {_.isNumber(score) && score}
                            </div>
                        ),
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: !bestMotorResponse,
                        hideStatuses: true,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroBestMotorResponse.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    bestMotorResponse: PropTypes.object,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsNeuroBestMotorResponse.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    bestMotorResponse: null,
    accepted: false,
    acceptDate: null,
};
// ObsNeuroBestMotorResponse.renderSectionScore = ({
export const renderNeuroBestMotorResponseSectionScore = ({
                                                             classes,
                                                             t,
                                                             section,
                                                             obs,
                                                             status,
                                                             score,
                                                             accepted,
                                                         }) => {
    let color = 'neuro';
    if (score >= 4) {
        color = 'neuroLow';
    } else if (score === 3) {
        color = 'neuroMedium';
    } else if (score >= 1) {
        color = 'neuroHigh';
    }

    return (score && (
        <div
            className={clsx([
                classes.sectionScore,
                color,
                accepted && classes.sectionScoreSelected,
            ])}>
            {_.isUndefined(score) && <span>&nbsp;</span>}
            {_.isNumber(score) && score}
        </div>
    ));
};

export default React.memo(ObsNeuroBestMotorResponse);